import {
  GetDocument,
  GetDocumentsByStatus,
  GetDocumentSignatureStatus,
  GetDocumentSignatures,
  AddDocumentSignature,
  UpdateDocumentField,
  GetFlattenedPdf,
  GetPdfVersions,
  RegeneratePdf,
  DeleteDocument,
} from "../../DriveScout/API/models/DocumentModel";

const state = {
  documentList: [],
  currentDocument: null,
  signatureStatus: null,
  signatures: [],
  loading: false,
  error: null,
};

const mutations = {
  SET_DOCUMENT_LIST(state, documents) {
    state.documentList = documents;
  },
  SET_CURRENT_DOCUMENT(state, document) {
    state.currentDocument = document;
  },
  SET_SIGNATURE_STATUS(state, status) {
    state.signatureStatus = status;
  },
  SET_SIGNATURES(state, signatures) {
    state.signatures = signatures;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_DOCUMENT_DATA(state) {
    state.currentDocument = null;
    state.signatureStatus = null;
    state.signatures = [];
    state.error = null;
  },
};

const actions = {
  /**
   * Get documents for a specific user
   * Backend: UserDocumentController@index returns { data: [...] }
   */
  GET_DOCUMENTS_BY_STATUS({ commit }, params) {
    commit("SET_LOADING", true);
    return GetDocumentsByStatus(params)
      .then((response) => {
        // Backend wraps list in 'data' property
        const documents = response.data?.data || response.data || [];
        commit("SET_DOCUMENT_LIST", documents);
        return documents;
      })
      .catch((error) => {
        commit("SET_ERROR", error.message || "Failed to load documents");
        throw error;
      })
      .finally(() => {
        commit("SET_LOADING", false);
      });
  },

  /**
   * Get a single document by ID
   * Backend: DocumentInstanceController@show returns { data: { document: {...} } }
   */
  GET_DOCUMENT({ commit }, documentId) {
    commit("SET_LOADING", true);
    return GetDocument(documentId)
      .then((response) => {
        // Backend nests document under data.document
        const document =
          response.data?.data?.document ||
          response.data?.document ||
          response.data;
        commit("SET_CURRENT_DOCUMENT", document);
        return document;
      })
      .catch((error) => {
        commit("SET_ERROR", error.message || "Failed to load document");
        throw error;
      })
      .finally(() => {
        commit("SET_LOADING", false);
      });
  },

  /**
   * Get signature status for a document
   * Backend: DocumentSignatureController@status returns { data: {...} }
   */
  GET_SIGNATURE_STATUS({ commit }, documentId) {
    return GetDocumentSignatureStatus(documentId)
      .then((response) => {
        // Backend wraps status in 'data' property
        const status = response.data?.data || response.data;
        commit("SET_SIGNATURE_STATUS", status);
        return status;
      })
      .catch((error) => {
        commit("SET_ERROR", error.message || "Failed to load signature status");
        throw error;
      });
  },

  /**
   * Get all signatures for a document
   * Backend: DocumentSignatureController@index returns:
   * { signatures, pending_signatures, requirements, completion_percentage }
   * All signatures now include document_field_id (field-level)
   */
  GET_SIGNATURES({ commit }, documentId) {
    return GetDocumentSignatures(documentId)
      .then((response) => {
        const backendData = response.data;

        // Transform backend response to frontend-expected structure
        // All signatures are field-level (have document_field_id)
        const transformedData = {
          signatures: {
            document_level: [],
            field_level: backendData.signatures || [],
          },
          pending_signatures: backendData.pending_signatures || [],
          requirements: backendData.requirements || [],
          completion_percentage: backendData.completion_percentage || 0,
        };

        commit("SET_SIGNATURES", transformedData.signatures);
        return transformedData;
      })
      .catch((error) => {
        commit("SET_ERROR", error.message || "Failed to load signatures");
        throw error;
      });
  },

  /**
   * Add a signature to a document
   * Backend returns: { message, data: { signature_id, is_fully_signed, fully_signed_at } }
   */
  ADD_SIGNATURE({ dispatch }, { documentId, signatureData }) {
    return AddDocumentSignature(documentId, signatureData)
      .then((response) => {
        dispatch(
          "ui/ADD_FLASH_MESSAGE",
          {
            type: "success",
            message: response.data?.message || "Signature added successfully!",
          },
          { root: true },
        );

        // Refresh signature status and signatures after adding
        dispatch("GET_SIGNATURE_STATUS", documentId);
        dispatch("GET_SIGNATURES", documentId);

        return response.data;
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to save signature. Please try again.";
        dispatch(
          "ui/ADD_FLASH_MESSAGE",
          {
            type: "error",
            message: errorMessage,
          },
          { root: true },
        );
        throw error;
      });
  },

  /**
   * Update a non-signature field value
   * Backend: DocumentInstanceController@updateField
   * Returns: { message, data: { field: {...}, document: {...} } }
   */
  UPDATE_FIELD({ dispatch }, { documentId, fieldName, value }) {
    return UpdateDocumentField(documentId, fieldName, value)
      .then((response) => {
        dispatch(
          "ui/ADD_FLASH_MESSAGE",
          {
            type: "success",
            message: response.data?.message || "Field updated successfully!",
          },
          { root: true },
        );

        // Refresh document data after update
        dispatch("GET_DOCUMENT", documentId);
        dispatch("GET_SIGNATURE_STATUS", documentId);

        return response.data;
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to save field. Please try again.";
        dispatch(
          "ui/ADD_FLASH_MESSAGE",
          {
            type: "error",
            message: errorMessage,
          },
          { root: true },
        );
        throw error;
      });
  },

  CLEAR_DOCUMENT_DATA({ commit }) {
    commit("CLEAR_DOCUMENT_DATA");
  },

  /**
   * Get the latest flattened PDF for a completed document
   * Backend: DocumentPdfController@getLatestFlattened
   * Returns: { data: { file_url, is_flattened, ... } }
   */
  GET_FLATTENED_PDF({ commit }, documentId) {
    return GetFlattenedPdf(documentId)
      .then((response) => {
        const pdfData = response.data?.data || response.data;
        return pdfData;
      })
      .catch((error) => {
        commit(
          "SET_ERROR",
          error.response?.data?.message ||
            error.message ||
            "Failed to get flattened PDF",
        );
        throw error;
      });
  },

  /**
   * Get all PDF versions for a document
   * Backend: DocumentPdfController@listVersions
   * Returns: { data: [...] }
   */
  GET_PDF_VERSIONS({ commit }, documentId) {
    return GetPdfVersions(documentId)
      .then((response) => {
        const versions = response.data?.data || response.data || [];
        return versions;
      })
      .catch((error) => {
        commit(
          "SET_ERROR",
          error.response?.data?.message ||
            error.message ||
            "Failed to get PDF versions",
        );
        throw error;
      });
  },

  /**
   * Request regeneration of the flattened PDF
   * Backend: DocumentPdfController@regenerate
   * Returns 202 Accepted - PDF is queued for generation
   */
  REGENERATE_PDF({ dispatch }, documentId) {
    return RegeneratePdf(documentId)
      .then((response) => {
        dispatch(
          "ui/ADD_FLASH_MESSAGE",
          {
            type: "info",
            message:
              "PDF regeneration has been queued. Please check back shortly.",
          },
          { root: true },
        );
        return response.data?.data || response.data;
      })
      .catch((error) => {
        dispatch(
          "ui/ADD_FLASH_MESSAGE",
          {
            type: "error",
            message:
              error.response?.data?.message || "Failed to regenerate PDF",
          },
          { root: true },
        );
        throw error;
      });
  },

  /**
   * Delete a document instance
   * Backend: DocumentInstanceController@destroy
   */
  DELETE_DOCUMENT({ dispatch, commit }, documentId) {
    return DeleteDocument(documentId)
      .then((response) => {
        dispatch(
          "ui/ADD_FLASH_MESSAGE",
          {
            type: "success",
            message: "Document deleted successfully",
          },
          { root: true },
        );
        commit("CLEAR_DOCUMENT_DATA");
        return response.data;
      })
      .catch((error) => {
        dispatch(
          "ui/ADD_FLASH_MESSAGE",
          {
            type: "error",
            message:
              error.response?.data?.message || "Failed to delete document",
          },
          { root: true },
        );
        throw error;
      });
  },
};

const getters = {
  documentList: (state) => state.documentList,
  currentDocument: (state) => state.currentDocument,
  signatureStatus: (state) => state.signatureStatus,
  signatures: (state) => state.signatures,
  loading: (state) => state.loading,
  error: (state) => state.error,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
